import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

import {
  SIDEBAR_ICON_ACTIVE,
  SIDEBAR_ICON_INACTIVE,
  SIDEBAR_ITEM_BASE,
  SIDEBAR_MODULE_ACTIVE,
  SIDEBAR_MODULE_INACTIVE,
  SIDEBAR_MODULE_LAYOUT,
  SIDEBAR_SUBMODULE_ACTIVE,
  SIDEBAR_SUBMODULE_INACTIVE,
  SIDEBAR_SUBMODULE_LAYOUT,
} from './sidebar-item.styles';

export type SidebarNavItemVariant = 'module' | 'link' | 'submodule';

/**
 * Ítem de navegación del sidebar. Cubre las tres variantes visuales del
 * rediseño con un único componente que comparte clases Tailwind (definidas en
 * `sidebar-item.styles.ts`) en vez de repetirlas por variante:
 * - `module`: botón que expande/colapsa un módulo con sub-ítems (ícono +
 *   etiqueta + chevron). El estado activo lo calcula el padre (no navega).
 * - `link`: módulo de primer nivel sin hijos, navega directo (ícono +
 *   etiqueta). El estado activo lo resuelve `routerLinkActive`.
 * - `submodule`: hijo dentro del árbol expandido (solo texto, indentado). El
 *   estado activo también lo resuelve `routerLinkActive`.
 *
 * IMPORTANTE: `classesFor(isActive)` arma el `class` completo eligiendo UN
 * ÚNICO bloque de estado (activo o inactivo). Nunca se concatenan las clases
 * de ambos estados en el mismo elemento — eso es lo que causaba que el color
 * de texto activo perdiera contra el color base (ver `sidebar-item.styles.ts`).
 * Por el mismo motivo, `link`/`submodule` NO usan `[routerLinkActive]="..."`
 * para que Angular agregue clases por su cuenta: se lee `rla.isActive` desde
 * `#rla="routerLinkActive"` y se recalcula el `class` completo en este
 * componente, que es la única fuente de verdad del string final.
 */
@Component({
  selector: 'app-sidebar-nav-item',
  templateUrl: './sidebar-nav-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive],
})
export class SidebarNavItemComponent {
  variant = input<SidebarNavItemVariant>('link');
  label = input.required<string>();
  route = input<string | null>(null);
  iconPath = input<string | null>(null);
  /** Sidebar colapsado: se oculta la etiqueta y el chevron, sólo queda el ícono. */
  collapsed = input(false);
  /** Sólo aplica a `module`: si el panel de sub-ítems está expandido. */
  expanded = input(false);
  /** Sólo aplica a `module`: el padre resuelve la activación (no hay routerLink). */
  active = input(false);

  /** Sólo se emite en variante `module` (click en el botón toggle). */
  activate = output<void>();

  protected readonly isModule = computed(() => this.variant() === 'module');
  protected readonly isSubmodule = computed(() => this.variant() === 'submodule');

  /**
   * Arma el `class` final para el estado dado. `isActive` llega como
   * parámetro (no como signal) porque en `link`/`submodule` proviene de
   * `rla.isActive` (template ref de `routerLinkActive`), y en `module` del
   * input `active()` calculado por el padre.
   */
  protected classesFor(isActive: boolean): string {
    if (this.isSubmodule()) {
      const state = isActive ? SIDEBAR_SUBMODULE_ACTIVE : SIDEBAR_SUBMODULE_INACTIVE;
      return `${SIDEBAR_ITEM_BASE} ${SIDEBAR_SUBMODULE_LAYOUT} ${state}`;
    }

    const justify = this.isModule()
      ? this.collapsed()
        ? 'justify-center'
        : 'justify-between'
      : this.collapsed()
        ? 'justify-center'
        : '';
    const state = isActive ? SIDEBAR_MODULE_ACTIVE : SIDEBAR_MODULE_INACTIVE;
    return `${SIDEBAR_ITEM_BASE} ${SIDEBAR_MODULE_LAYOUT} ${justify} ${state}`.replace(/\s+/g, ' ').trim();
  }

  protected iconClasses(isActive: boolean): string {
    return isActive ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON_INACTIVE;
  }
}
